import { FC, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetCurrencies } from "../../../../../../api/services/program/programApis";
import { useGetProjectFeeDetails } from "../../../../../../api/services/project/projectApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
} from "../../../../../../components";
import { ExpandCollapseButton } from "../../../../../../components/expandCollapseButton/ExpandCollapseButton";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { TaxDetails } from "../../../../../../components/taxDetails/TaxDetails";
import {
  COUNTRY_CURRENCY_MAP,
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../../../components/textFieldCurrency/CurrencyUtils";
import {
  FORM_ERROR_TIMEOUT_LONG,
  MODULE_PERMISSIONS,
  TIMEOUT_300,
} from "../../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../../helpers/constants/siteCoreKeys";
import {
  getFormattedAmount,
  getIdByNameFromList,
  getNameByIdFromList,
  isMilestoneBilling,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import useAccordion from "../../../../../../hooks/useAccordion";
import { useForceUpdate } from "../../../../../../hooks/useForceUpdate";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { keyDetailsSelector } from "../../../../../../redux/slices/project/projectSelector";
import {
  billingConfigurationDetailsFormKeys,
  ProjectFeeSectionKey,
  ProjectFeeTabFormKey,
  taxDetailsFormKeys,
} from "../../../../modelsAndSchemas/Models";
import { projectFeeDetailsCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import FeeDetails from "./components/feeDetails/FeeDetails";

interface ProjectFeeTabProps {
  pageId: string;
  projectId: string;
  isEditProject?: boolean;
  refetchProjectFee: boolean;
}

const initialAccordionStatus = {
  feeDetails: true,
  taxDetails: false,
};

export const ProjectFeeTab: FC<ProjectFeeTabProps> = ({
  pageId,
  isEditProject = false,
  projectId,
  refetchProjectFee,
}) => {
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    handleAccordionExpansionState,
  } = useAccordion(initialAccordionStatus);

  const [defaultUserCurrencyId, setDefaultUserCurrencyId] = useState("");
  const keyDetails = useSelector(keyDetailsSelector);
  const forceUpdate = useForceUpdate();

  const dispatch = useDispatch();

  const { getValues, setValue, reset } = useFormContext();

  const [projectFeeForTax, setProjectFeeForTax] = useState<string>("");

  const taxDetailsRef = useRef<{
    resetData: () => void;
  }>(null);

  const {
    data: projectFeeDetailsData,
    isFetching: isProjectFeeDetailsPending,
    isError: isProjectFeeDetailsError,
    isSuccess: isProjectFeeDetailsDataSuccess,
    refetch: refetchProjectFeeDetailsData,
    refetchWithParams: getProjectFeeDetails,
  } = useGetProjectFeeDetails();

  const { fetchComponentTitle, commonLabels } = useSiteCore();
  const { hasModulePermission } = usePermissions();

  const { data: currenciesList } = useGetCurrencies();

  useEffect(() => {
    if (isNotEmpty(projectId)) {
      getProjectFeeDetails(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (isProjectFeeDetailsDataSuccess && refetchProjectFee) {
      refetchProjectFeeDetailsData();
    }
  }, [refetchProjectFee]);

  useEffect(() => {
    if (isNotEmpty(currenciesList) && !isEditProject) {
      const currencyId = getIdByNameFromList(
        currenciesList,
        COUNTRY_CURRENCY_MAP.US,
      );
      if (isNotEmpty(currencyId)) {
        setDefaultUserCurrencyId(currencyId);
        setValue(ProjectFeeTabFormKey.currencyId, currencyId);
      }
    }
  }, [currenciesList, isEditProject]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isProjectFeeDetailsPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isProjectFeeDetailsPending]);

  useEffect(() => {
    if (isProjectFeeDetailsError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProjectFeeDetailsError]);

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={labelKey} />;
  };

  const {
    formState: { errors },
  } = useFormContext();

  const projectFeeSectionFieldsMap: Record<ProjectFeeSectionKey, string[]> = {
    feeDetails: [
      ProjectFeeTabFormKey.billingTypeId,
      ProjectFeeTabFormKey.billingTriggerId,
      ProjectFeeTabFormKey.earlyPayDays,
      ProjectFeeTabFormKey.earlyPayPercentage,
      ProjectFeeTabFormKey.minimumTransferFee,
      ProjectFeeTabFormKey.proBonoCategoryId,
      ProjectFeeTabFormKey.currencyId,
      ProjectFeeTabFormKey.fee,
      ProjectFeeTabFormKey.feeWithFormat,
      ProjectFeeTabFormKey.schedulePeriod,
      ProjectFeeTabFormKey.feePaymentType,
      ProjectFeeTabFormKey.schedules,
      ProjectFeeTabFormKey.milestones,
      ProjectFeeTabFormKey.activities,
      ProjectFeeTabFormKey.feePercentage,
      ProjectFeeTabFormKey.amount,
      ProjectFeeTabFormKey.amountWithFormat,
      ProjectFeeTabFormKey.scheduleFrequency,
      ProjectFeeTabFormKey.billingSchedules,
      ProjectFeeTabFormKey.description,
    ],
    taxDetails: [
      ProjectFeeTabFormKey.billingTypeId,
      ProjectFeeTabFormKey.billingTriggerId,
      ProjectFeeTabFormKey.currencyId,
      ProjectFeeTabFormKey.fee,
    ],
  };

  const hasErrors = (section: ProjectFeeSectionKey) => {
    const fields = projectFeeSectionFieldsMap[section];
    return fields.some((field) => errors[field]);
  };

  useEffect(() => {
    (Object.keys(projectFeeSectionFieldsMap) as ProjectFeeSectionKey[]).forEach(
      (sectionKey: ProjectFeeSectionKey) => {
        if (hasErrors(sectionKey)) {
          handleAccordionExpansionState(sectionKey.toString(), true);
        }
      },
    );
  }, [errors]);

  useEffect(() => {
    if (isNotEmpty(projectFeeDetailsData) && isProjectFeeDetailsDataSuccess) {
      const {
        billingTypeId,
        billingTriggerId,
        currency,
        feePaymentType,
        schedulePeriod,
        earlyPayDays,
        earlyPayPercentage,
        minimumTransferFee,
        proBonoCategoryId,
        fee,
        taxes,
        id,
      } = projectFeeDetailsData;

      const billingSchedules = isMilestoneBilling(billingTypeId)
        ? projectFeeDetailsData.billingSchedules?.map((schedule) => ({
            ...schedule,
            activities: schedule.topics,
          }))
        : projectFeeDetailsData.billingSchedules;

      const feeWithFormat = `${getFormattedAmount(
        fee ?? 0,
        CURRENCY_FORMAT_MAP[currency?.name]?.locale,
      )}`;
      const minimumTransferFeeFormat = `${getFormattedAmount(
        minimumTransferFee ?? 0,
        CURRENCY_FORMAT_MAP[currency?.name]?.locale,
      )}`;

      setValue(ProjectFeeTabFormKey.billingTypeId, billingTypeId);

      setTimeout(() => {
        setValue(ProjectFeeTabFormKey.currencyId, currency?.id);

        setValue(ProjectFeeTabFormKey.billingTriggerId, billingTriggerId);
        setValue(ProjectFeeTabFormKey.earlyPayDays, earlyPayDays?.toString());
        setValue(
          ProjectFeeTabFormKey.earlyPayPercentage,
          earlyPayPercentage?.toString(),
        );
        minimumTransferFee &&
          setValue(
            ProjectFeeTabFormKey.minimumTransferFee,
            minimumTransferFee?.toString(),
          );
        minimumTransferFee &&
          setValue(
            ProjectFeeTabFormKey.minimumTransferFeeWithFormat,
            minimumTransferFeeFormat,
          );
        setValue(ProjectFeeTabFormKey.proBonoCategoryId, proBonoCategoryId);
        setValue(ProjectFeeTabFormKey.schedulePeriod, schedulePeriod);
        setValue(ProjectFeeTabFormKey.feePaymentType, feePaymentType);
        setValue(ProjectFeeTabFormKey.id, id);
        setTimeout(() => {
          fee && setValue(ProjectFeeTabFormKey.fee, fee.toString());
          fee && setValue(ProjectFeeTabFormKey.feeWithFormat, feeWithFormat);
          reset(getValues());
        }, TIMEOUT_300);
        setValue(ProjectFeeTabFormKey.billingSchedules, billingSchedules);
        setValue(ProjectFeeTabFormKey.taxEntries, taxes);
      }, FORM_ERROR_TIMEOUT_LONG);
    }
  }, [projectFeeDetailsData, isProjectFeeDetailsDataSuccess]);

  const { currencyId } = getValues();

  const currencyName = getNameByIdFromList(currenciesList, currencyId);
  const currencyFormat: CurrencyFormat = CURRENCY_FORMAT_MAP[currencyName];

  useEffect(() => {
    setProjectFeeForTax(getValues(ProjectFeeTabFormKey.fee));
  }, [getValues(ProjectFeeTabFormKey.fee)]);

  return (
    <MuiBox>
      <ExpandCollapseButton
        accordionStatus={accordionStatus}
        handleExpandCollapseAllChange={handleExpandCollapseAllChange}
      />

      <PermissionWrapper
        permission={
          isEditProject
            ? MODULE_PERMISSIONS.PROJECT_EDIT_PROJECT_FEE
            : MODULE_PERMISSIONS.PROJECT_ADD_PROJECT_FEE
        }
      >
        <AccordionHeader
          wrapperClassName="accordion-header project-fee-header"
          title={renderHeader(
            fetchComponentTitle(projectFeeDetailsCoreKeys.projectFeeDetails),
          )}
          onChange={() => handleIndividualAccordionChange("feeDetails")}
          isExpanded={accordionStatus.feeDetails}
        >
          <FeeDetails
            pageId={pageId}
            clientId={keyDetails?.client.id ?? ""}
            currenciesList={currenciesList || []}
            defaultUserCurrency={defaultUserCurrencyId}
            currencyFormat={currencyFormat}
            currencyName={currencyName}
            forceUpdate={forceUpdate}
            currencyId={currencyId}
            billingConfigurationDetailsFormKeys={
              billingConfigurationDetailsFormKeys
            }
            projectFeeDetailsData={projectFeeDetailsData}
            isEditProject={isEditProject}
          />
        </AccordionHeader>
      </PermissionWrapper>

      <PermissionWrapper
        permission={
          isEditProject
            ? MODULE_PERMISSIONS.PROJECT_EDIT_TAX_DETAILS
            : MODULE_PERMISSIONS.PROJECT_ADD_TAX_DETAILS
        }
      >
        <AccordionHeader
          wrapperClassName="accordion-header"
          title={renderHeader(
            fetchComponentTitle(commonSiteCoreKeys.taxDetails),
          )}
          onChange={() => handleIndividualAccordionChange("taxDetails")}
          isExpanded={accordionStatus.taxDetails}
        >
          <TaxDetails
            ref={taxDetailsRef}
            pageId={pageId}
            currencyFormat={currencyFormat}
            currencyName={currencyName}
            forceUpdate={forceUpdate}
            currencyId={currencyId}
            isInEditMode={isEditProject}
            isInViewMode={
              isNotEmpty(projectFeeDetailsData) &&
              !projectFeeDetailsData?.istaxEditable
            }
            taxDetailsFormKeys={taxDetailsFormKeys}
            programOrProjectFee={projectFeeForTax}
            hasDeletePermission={hasModulePermission(
              MODULE_PERMISSIONS.PROJECT_DELETE_TAX_DETAILS,
            )}
          />
        </AccordionHeader>
      </PermissionWrapper>
    </MuiBox>
  );
};
